import { useState } from "react";
import { useCreateProperty, type CreatePropertyData } from "@/hooks/useProperties";
import { useCreateUnit, type CreateUnitData } from "@/hooks/useUnits";
import { useAuth } from "@/hooks/useAuth";
import { propertiesRepository } from "@/repositories/properties";
import { unitsRepository } from "@/repositories/units";
import type { RegaValidateResponse, RegaLocation } from "@/types/rega";
import { mapRegaToPropertyType, mapRegaToUnitType, mapRegaToListingType, mapRegaUsagesToPurpose } from "@/lib/rega-mappers";
import { getRegaAnnualRent, getRegaSaleTotal } from "@/lib/regaPricing";

function getLocation(loc: RegaLocation | string | undefined): RegaLocation | null {
  if (!loc || typeof loc === "string") return null;
  return loc;
}

export function buildPropertyFromRega(rega: RegaValidateResponse): CreatePropertyData {
  const loc = getLocation(rega.location);

  return {
    name: loc?.district
      ? `${rega.propertyType} - ${loc.district}`
      : `${rega.propertyType} - ${rega.adLicenseNumber}`,
    property_type: mapRegaToPropertyType(rega.propertyType),
    purpose: mapRegaUsagesToPurpose(rega.propertyUsages, mapRegaToPropertyType(rega.propertyType)),
    city: loc?.city,
    district: loc?.district,
    address: loc?.street,
    postal_code: loc?.postalCode,
    total_area: Number(rega.propertyArea) || undefined,
    deed_number: rega.deedNumber,
    latitude: loc?.latitude,
    longitude: loc?.longitude,
  };
}

export function buildUnitFromRega(
  rega: RegaValidateResponse,
  propertyId: string
): CreateUnitData {
  const listingType = mapRegaToListingType(rega.advertisementType);
  // REGA's propertyPrice is a per-m² rate for parcel-priced types (land,
  // school) — store the derived totals, never the raw rate (CEO 2026-07-12).
  const annualRent = getRegaAnnualRent(rega);
  const saleTotal = getRegaSaleTotal(rega);

  return {
    property_id: propertyId,
    unit_number: rega.landNumber || "1",
    unit_type: mapRegaToUnitType(rega.propertyType) as CreateUnitData["unit_type"],
    area: Number(rega.propertyArea) || undefined,
    rent_amount: listingType === "rent" ? annualRent : undefined,
    annual_rent: listingType === "rent" ? annualRent : undefined,
    sale_price: listingType === "sale" ? saleTotal : undefined,
    status: "available",
  };
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type RegaMatchResult = {
  propertyId: string;
  unitId: string | null;
  propertyName: string;
};

export type RegaResolveResult = {
  propertyId: string;
  unitId: string;
  /** Whether existing records were found or new ones created */
  wasCreated: boolean;
  propertyName?: string;
};

/** @deprecated — use matchByDeed + createFromRega instead */
export type RegaResolveOptions = {
  /**
   * When the user has already selected a property, pass its ID here.
   * The resolver will use that property directly and only find/create a unit
   * under it — it will NOT create a new property.
   */
  selectedPropertyId?: string;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides two separate operations for REGA property/unit handling:
 *
 * **matchByDeed** (used at license-validation time):
 *   Pure lookup — no DB writes. Searches for an existing property in the
 *   company by deed_number. If found, also returns the first unit under it
 *   (unambiguous pre-select). Returns null when no match.
 *
 * **createFromRega** (used at final submit):
 *   Creates a new property + unit from REGA data. Only called when the user
 *   submits without having selected any existing property.
 *
 * This separation prevents duplicate-property creation in the standalone
 * wizard: because matchByDeed never writes, validating a license in a fresh
 *  wizard (where listerData.propertyId is empty) is now safe.
 */
export function useRegaAutoCreate() {
  const { companyId } = useAuth();
  const createProperty = useCreateProperty();
  const createUnit = useCreateUnit();
  const [isMatching, setIsMatching] = useState(false);

  // -------------------------------------------------------------------------
  // matchByDeed — lookup only, never mutates
  // -------------------------------------------------------------------------
  const matchByDeed = async (
    rega: RegaValidateResponse,
  ): Promise<RegaMatchResult | null> => {
    if (!companyId) throw new Error("No company ID");
    const deedNumber = rega.deedNumber;
    if (!deedNumber) return null;

    setIsMatching(true);
    try {
      const existingProperty = await propertiesRepository.findActiveByDeed(
        companyId,
        deedNumber,
      );

      if (!existingProperty) return null;

      // Look for the first unit under the matched property (unambiguous pre-select)
      const existingUnits = await unitsRepository.listFirstActiveIdForProperty(
        existingProperty.id,
        companyId,
      );

      return {
        propertyId: existingProperty.id,
        unitId: existingUnits && existingUnits.length > 0 ? existingUnits[0].id : null,
        propertyName: existingProperty.name ?? "",
      };
    } finally {
      setIsMatching(false);
    }
  };

  // -------------------------------------------------------------------------
  // createFromRega — create property + unit from REGA data (submit path only)
  // -------------------------------------------------------------------------
  const createFromRega = async (
    rega: RegaValidateResponse,
  ): Promise<RegaResolveResult> => {
    if (!companyId) throw new Error("No company ID");

    const propertyData = buildPropertyFromRega(rega);
    const property = await createProperty.mutateAsync(propertyData);

    const unitData = buildUnitFromRega(rega, property.id);
    const unit = await createUnit.mutateAsync(unitData);

    return {
      propertyId: property.id,
      unitId: unit.id,
      wasCreated: true,
      propertyName: property.name,
    };
  };

  return {
    matchByDeed,
    createFromRega,
    isMatching,
    isPending: isMatching || createProperty.isPending || createUnit.isPending,
  };
}
